import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import {NzModalRef} from "ng-zorro-antd/modal";
import {TagService} from "../../../core/services/tag.service";
import {NzMessageService} from "ng-zorro-antd/message";
import {FormBuilder, FormGroup} from "@angular/forms";
import {CountryService} from "../../../core/services/country.service";
import {SettingService} from "../../../core/services/setting.service";
import {LoadingService} from "../../../core/services/loading.service";
import { ItineraryService } from '../../../core/services/itinerary.service';
import { SETTINGS } from 'src/app/core/config/common.settings';
import {geoJSONManager} from "../../../core/ult-functions/geo";
import {MapboxService} from "../../../core/services/mapbox.service";
import {JsReportService} from "../../../core/services/js-report.service";

@Component({
  selector: 'app-export-content',
  templateUrl: './export-content.component.html',
  styleUrls: ['./export-content.component.scss']
})
export class ExportContentComponent implements OnInit, OnDestroy {
  @Input() id: any;
  @Input() viewSettings: any;
  @Input() currentThemeType?: string;
  isButtonLoading = false;
  data: any;
  organizationSetting: any;
  protected countries: any = [];
  protected citiesByCountry: any = [];
  languages: any[] = [];
  countriesJson: any = [];
  exportForm!: FormGroup;

  exportFormats = [
    { label: 'PDF', value: 'pdf' },
    // { label: 'Word', value: 'word' }
  ];

  // view settings
  costingBoxChecked = false;
  mileageBoxChecked = false;
  travelTimeBoxChecked = false;
  vehicleNameBoxChecked = false;
  vehicleImageBoxChecked = false;
  remarksTipsBoxChecked = false;
  additionalNotesBoxChecked = false;
  itineraryTitleBoxChecked = false;
  tourOperatorBoxChecked = false;
  companyInformationBoxChecked = false;
  hotelRatingBoxChecked = false;
  hotelDescriptionBoxChecked = false;
  hotelWebsiteBoxChecked = false;
  hotelImageBoxChecked = false;
  websiteLinkBoxChecked = false;
  paymentLinkBoxChecked = false;
  customerInformationBoxChecked = false;
  displayMapTopsideBoxChecked = false;
  thumbnailBoxChecked = false;
  hideDaywiseInclusionsExclusionsBoxChecked = false;
  includeBadgeBoxChecked = false;
  excludeBadgeBoxChecked = false;
  pricingRemarksBoxChecked = false;
  accommodationNoteBoxChecked = true;
  accommodationRoomsBoxChecked = false;
  transportNoteBoxChecked = true;
  experiencesNoteBoxChecked = true;
  hideExperiencesTimesBoxChecked = false;
  mapType = "NAV";
  mapTypeOptions: any[] = [];
  private langChangeSub?: Subscription;
  departureBoxChecked = true;
  protected readonly SETTINGS = SETTINGS;



  constructor(
    private modal: NzModalRef,
    private nzMessageService: NzMessageService,
    private fb: FormBuilder,
    private settingService: SettingService,
    private loading: LoadingService,
    private itineraryService: ItineraryService,
    private mapBoxService: MapboxService,
    private reportService: JsReportService,
    private countryService: CountryService,
    private translocoService: TranslocoService
  ) { }


  private getMapTypeOptions(): Array<{ value: string; label: string }> {
    const lang = this.translocoService.getActiveLang();
    const f = SETTINGS.MAP_TYPES.filter((i: any) => i.language === lang);
    return (f.length ? f : SETTINGS.MAP_TYPES.filter((i: any) => i.language === 'en'))
      .map(({ value, label }: any) => ({ value, label }));
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }
  ngOnInit(): void {
    this.mapTypeOptions = this.getMapTypeOptions();
    this.langChangeSub = this.translocoService.langChanges$.subscribe(() => {
      this.mapTypeOptions = this.getMapTypeOptions();
    });
    this.getCountries().then();
    this.formInit();
    this.setViewSettings();
  }

  handleCancel(): void {
    this.modal.close({ message: "error" });
  }

  setViewSettings(): void {
    this.tourOperatorBoxChecked = this.viewSettings.isTourOperatorHidden;
    this.costingBoxChecked = this.viewSettings?.isTourCostHidden;
    this.companyInformationBoxChecked = this.viewSettings?.isCompanyDetailsHidden;
    this.departureBoxChecked = this.viewSettings?.isDepartureChecked;
    this.mileageBoxChecked = this.viewSettings?.isMileageChecked;
    this.travelTimeBoxChecked = this.viewSettings?.isTravelTimeChecked;
    this.vehicleNameBoxChecked = this.viewSettings?.isVehicleNameChecked;
    this.vehicleImageBoxChecked = this.viewSettings?.isVehicleImageChecked;
    this.remarksTipsBoxChecked = this.viewSettings?.isRemarksTipsChecked;
    this.additionalNotesBoxChecked = this.viewSettings?.isAdditionalNotesChecked ?? false;
    this.itineraryTitleBoxChecked = this.viewSettings?.isItineraryTitleChecked;
    this.hotelRatingBoxChecked = this.viewSettings?.isHotelRatingChecked;
    this.hotelDescriptionBoxChecked = this.viewSettings?.isHotelDescriptionChecked;
    this.hotelWebsiteBoxChecked = this.viewSettings?.isHotelWebsiteChecked;
    this.hotelImageBoxChecked = this.viewSettings?.isHotelImageChecked;
    this.websiteLinkBoxChecked = this.viewSettings?.isWebsiteLinkChecked;
    this.paymentLinkBoxChecked = this.viewSettings?.isPaymentLinkChecked;
    this.customerInformationBoxChecked = this.viewSettings?.isCustomerInformationChecked;
    this.displayMapTopsideBoxChecked = this.viewSettings?.isDisplayMapTopsideChecked;
    this.thumbnailBoxChecked = this.viewSettings?.isThumbnailChecked ?? false;
    this.hideDaywiseInclusionsExclusionsBoxChecked = this.viewSettings?.isDaywiseInclusionsExclusionsChecked ?? false;
    this.includeBadgeBoxChecked = this.viewSettings?.isIncludeBadgeChecked ?? false;
    this.excludeBadgeBoxChecked = this.viewSettings?.isExcludeBadgeChecked ?? false;
    this.accommodationNoteBoxChecked = this.viewSettings?.isAccommodationNoteChecked ?? true;
    this.accommodationRoomsBoxChecked = this.viewSettings?.isAccommodationRoomsChecked ?? true;
    this.transportNoteBoxChecked = this.viewSettings?.isTransportNoteChecked ?? true;
    this.experiencesNoteBoxChecked = this.viewSettings?.isExperiencesNoteChecked ?? true;
    this.hideExperiencesTimesBoxChecked = this.viewSettings?.isExperiencesTimesChecked ?? false;
    this.pricingRemarksBoxChecked = this.viewSettings?.isPricingRemarksChecked ?? false;
    this.mapType = this.viewSettings?.mapType || "NAV";
  }

  formInit(): void {
    this.exportForm = this.fb.group({
      // Destination
      dest_Description: [true],
      dest_Image: [true],

      // Experiences
      exp_Image: [true],
      exp_Description: [true],

      // General
      gen_DisplayMap: [true],
      gen_ItineraryBannerImage: [true],
      gen_SummaryTable: [true],

      // Export Format
      exportFormat: ['pdf']   // default PDF
    });
  }


  // async generatePDF(): Promise<void> {
  //   this.isButtonLoading = true;
  //   try {
  //     let mapUrl: any = null;
  //     Promise.all([this.loadItineraryByID(this.id)]).then(async () => {
  //       mapUrl = this.data.viewSettings.mapType === SETTINGS.MAP_TYPES[0].value ?  await this.mapDirectionsImage() : this.mapImage();
  //       const countryImageURL = this.data?.tour?.customer?.country?.code ?
  //         this.findCountryFlagImage(this.data?.tour.customer.country.code) : "";
  //       const b2b = this.data?.tour?.type === "B2B" ? {
  //         itinerary: this.data?.itinerary,
  //         companyName: this.data?.b2b?.customer?.companyName || "",
  //         logo: this.data?.b2b?.customer?.logo,
  //         costing: this.data?.b2b?.costing,
  //         companyDetails: {summary: this.data?.b2b?.customer?.summary || "" , websiteLink: this.data?.b2b?.customer?.websiteLink || ""},
  //       } : "";
  //       const payload = {
  //         day: this.data.days.length,
  //         night: this.data.days.length - 1,
  //         ...this.data,
  //         ...this.organizationSetting,
  //         citiesByCountry: this.citiesByCountry,
  //         countries: this.countries,
  //         mapURL: mapUrl,
  //         countryImageURL,
  //         ...b2b
  //       };
  //       this.reportService.generateItineraryReport(payload).then((res) => {
  //         const fileName = `${this.data.tour.referenceNo}-${this.data?.b2b?.customer?.companyName ? this.data?.b2b?.customer?.companyName : "No Name"} `;
  //         this.reportService.downloadReport(res, fileName);
  //         this.loading.stop();
  //       });
  //     });
  //     this.nzMessageService.success("Downloaded successfully.");
  //     this.isButtonLoading = false;
  //     this.modal.close({ message: "success" });
  //   } catch (error: any) {
  //     this.isButtonLoading = false;
  //     this.nzMessageService.error(error.message.message);
  //     this.modal.close({ message: "error" });
  //   }
  // }

  async generatePDF(): Promise<void> {
    this.loading.start();
    this.isButtonLoading = true;

    try {
      // Load data first
      await this.loadItineraryByID(this.id);

      const tour = { ...this.data?.tour, themeType: this.currentThemeType };

      const mapUrl =
        this.mapType === SETTINGS.MAP_TYPES[0].value
          ? await this.mapDirectionsImage()
          :  this.mapImage();
      const countryCode = this.data?.tour?.customer?.country?.code;
      const countryImageURL = countryCode
        ? this.findCountryFlagImage(countryCode)
        : "";

      const b2b =
        this.data?.tour?.type === "B2B"
          ? {
            itinerary: this.data?.itinerary,
            companyName: this.data?.b2b?.customer?.companyName ?? "",
            logo: this.data?.b2b?.customer?.logo ?? "",
            costing: this.data?.b2b?.costing,
            companyDetails: {
              summary: this.data?.b2b?.customer?.summary ?? "",
              websiteLink: this.data?.b2b?.customer?.websiteLink ?? "",
            },
          }
          : undefined;

      const daysCount = this.data?.days?.length ?? 0;

      const settings = {
        ...(this.viewSettings || {}),
        isTourCostHidden: this.costingBoxChecked,
        // isTourOperatorHidden: this.tourOperatorBoxChecked,
        // isCompanyDetailsHidden: this.companyInformationBoxChecked,
        isDepartureChecked: this.departureBoxChecked,
        isMileageChecked: this.mileageBoxChecked,
        isTravelTimeChecked: this.travelTimeBoxChecked,
        isVehicleNameChecked: this.vehicleNameBoxChecked,
        isVehicleImageChecked: this.vehicleImageBoxChecked,
        isRemarksTipsChecked: this.remarksTipsBoxChecked,
        isAdditionalNotesChecked: this.additionalNotesBoxChecked,
        isItineraryTitleChecked: this.itineraryTitleBoxChecked,
        isHotelRatingChecked: this.hotelRatingBoxChecked,
        isHotelDescriptionChecked: this.hotelDescriptionBoxChecked,
        isHotelWebsiteChecked: this.hotelWebsiteBoxChecked,
        isHotelImageChecked: this.hotelImageBoxChecked,
        isWebsiteLinkChecked: this.websiteLinkBoxChecked,
        isPaymentLinkChecked: this.paymentLinkBoxChecked,
        isCustomerInformationChecked: this.customerInformationBoxChecked,
        isDisplayMapTopsideChecked: this.displayMapTopsideBoxChecked,
        isThumbnailChecked: this.thumbnailBoxChecked,
        isDaywiseInclusionsExclusionsChecked: this.hideDaywiseInclusionsExclusionsBoxChecked,
        isIncludeBadgeChecked: this.includeBadgeBoxChecked,
        isExcludeBadgeChecked: this.excludeBadgeBoxChecked,
        isAccommodationNoteChecked: this.accommodationNoteBoxChecked,
        isAccommodationRoomsChecked: this.accommodationRoomsBoxChecked,
        isTransportNoteChecked: this.transportNoteBoxChecked,
        isExperiencesNoteChecked: this.experiencesNoteBoxChecked,
        isExperiencesTimesChecked: this.hideExperiencesTimesBoxChecked,
        isPricingRemarksChecked: this.pricingRemarksBoxChecked,
        mapType: this.mapType
      };

      const payload = {
        day: daysCount,
        night: Math.max(0, daysCount - 1),
        clientTimezoneOffset: new Date().getTimezoneOffset(),
        ...this.data,
        viewSettings: settings,
        ...this.organizationSetting,
        citiesByCountry: this.citiesByCountry,
        countries: this.countries,
        mapURL: mapUrl,
        countryImageURL,
        tour,
        ...(b2b ? { b2b } : {}),
        pdfViewOptions: this.exportForm.getRawValue()
      };
      const res = await this.reportService.generateItineraryReport(payload);

      const company = this.data?.b2b?.customer?.companyName?.trim();
      const fileName = `${this.data?.tour?.referenceNo ?? "Reference"}-${company || "No Name"}`;

      await this.reportService.downloadReport(res, fileName);

      this.nzMessageService.success(
        this.translocoService.translate('shared.exportContent.messages.downloadedSuccessfully')
      );
      this.modal.close({ message: "success" });
    } catch (error: any) {
      const msg =
        error?.message?.message ||
        error?.message ||
        this.translocoService.translate('shared.exportContent.messages.somethingWentWrong');
      this.nzMessageService.error(msg);
      this.modal.close({ message: "error" });
    } finally {
      this.isButtonLoading = false;
      this.loading.stop();
    }
  }


  async loadItineraryByID(rId: string): Promise<any> {
    try {
      this.data = await this.itineraryService.getItineraryById({
        id: rId,
      });
      await this.settingService.findBrand(this.data.creator[0].creatorId);
      this.settingService.onBrandChange.subscribe((brand: any) => {
        this.organizationSetting = brand;
      });
      this.countrySequence();
      this.citySequence();
    } catch (e) {

    }
  }


  countrySequence(): void {
    this.data.days.forEach((day: any) => {
      const country = day?.destination?.country;
      if (!this.countries.includes(country) && country !== undefined) {
        this.countries.push(country);
      }
    });
  }

  citySequence(): void {
    this.data.days.forEach((day: any) => {
      const country = day?.destination?.country;
      const city = day?.destination?.city;

      const countryIndex = this.citiesByCountry.findIndex((item: any) => item?.country === country);

      if (countryIndex === -1) {
        this.citiesByCountry.push({country, cities: [city]});
      } else {
        // If country already exists, add city only if it's not already present
        if (!this.citiesByCountry[countryIndex].cities.includes(city)) {
          this.citiesByCountry[countryIndex].cities.push(city);
        }
      }
    });
  }

  mapImage(): string {
    let geoJSON: any | undefined;

    if (this.data?.days[0]?.transport.length !== 0){
      const {locationFrom} = this.data?.days[0]?.transport[0];
      if (locationFrom){
        geoJSON = geoJSONManager(geoJSON, parseFloat(locationFrom?.latitude), parseFloat(locationFrom?.longitude));
      }
    }

    this.data.days.forEach((day: any) => {
      if (day?.destination) {
        const {location} = day?.destination;
        geoJSON = geoJSONManager(geoJSON, parseFloat(location?.latitude), parseFloat(location?.longitude));
      }
    });

    const lastDay = this.data.days.length - 1;
    if (this.data?.days[lastDay]?.transport.length !== 0){
      const lastTransport = this.data?.days[lastDay]?.transport.length - 1;
      const {locationTo} = this.data?.days[lastDay]?.transport[lastTransport];
      if (locationTo){
        geoJSON = geoJSONManager(geoJSON, parseFloat(locationTo?.latitude), parseFloat(locationTo?.longitude));
      }
    }

    return this.mapBoxService.getStaticMapURL(geoJSON);
  }

  async mapDirectionsImage(): Promise<any> {
    try {
      // Collect all waypoints from the itinerary
      const waypoints: Array<{lat: number, lng: number}> = [];

      // Add starting point from first transport
      if (this.data?.days[0]?.transport.length !== 0) {
        const {locationFrom} = this.data?.days[0]?.transport[0];
        if (locationFrom) {
          waypoints.push({
            lat: parseFloat(locationFrom.latitude),
            lng: parseFloat(locationFrom.longitude)
          });
        }
      }

      let prevId: string | undefined;
      // Add destination points
      for (const day of this.data?.days ?? []) {
        const dest = day?.destination;
        if (!dest?.location) {
          continue;
        }

        const curId = dest._id as string | undefined;
        if (curId && curId === prevId) {
          continue;
        }

        const {latitude, longitude} = dest.location;
        const lat = typeof latitude === "number" ? latitude : parseFloat(latitude);
        const lng = typeof longitude === "number" ? longitude : parseFloat(longitude);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          waypoints.push({lat, lng});
        }
        prevId = curId;
      }

      // Add ending point from last transport
      const lastDay = this.data.days.length - 1;
      if (this.data?.days[lastDay]?.transport.length !== 0) {
        const lastTransport = this.data?.days[lastDay]?.transport.length - 1;
        const {locationTo} = this.data?.days[lastDay]?.transport[lastTransport];
        if (locationTo) {
          waypoints.push({
            lat: parseFloat(locationTo.latitude),
            lng: parseFloat(locationTo.longitude)
          });
        }
      }
      return await this.mapBoxService.getStaticMapWithDirections(false, waypoints);
    } catch (e) {
      console.error('Error generating map image with directions:', e);
      return null;
    }
  }

  findCountryFlagImage(countryCode: string): string {
    const selected_country = this.countriesJson.find(
      (c: any) => c.code === countryCode
    );
    return selected_country?.image;
  }

  async getCountries(): Promise<void> {
    this.countriesJson = await this.countryService.getCountries();
  }
}
